import api from "../../api.service"
import { getPaginatedResponse } from "../utils"

import queryString from "query-string"

/**
 *
 * Get all action items from the projects the user is included on.
 *
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.select] {string|string[]}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getActionItems(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            if (err.name === "CanceledError") {
                return {
                    value: [],
                    nextLink: null,
                    hasNext: () => false,
                }
            } else {
                throw api.getResponseError(err)
            }
        })
}

/**
 * One page of Home's Task status: overdue, then in progress, then done, across my
 * active projects. Every task on projects I manage, only mine on the others.
 *
 * @param [queryParams] {object}
 * @param [queryParams.page] {number} From 0
 * @param [queryParams.limit] {number} Up to 50, defaults to 10
 * @param [queryParams.mission] {string} Only this project's tasks
 * @return {Promise<{value: object[], missions: object, hasNext: boolean, counts: object}>}
 */
export function getActionItemStatus(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems/status?${qs}`, { signal })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 *
 * Get all action items from the projects the user is included on.
 *
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getActionItemsLite(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems/lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getActionItemsLiteDates(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems/dates-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getActionItemsBudgetLean(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems/budget-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.select] {string|string[]}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getMyActionItems(queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/actionItems/my?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * @param orgId {string}
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.select] {string|string[]}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getOrgActionItems(orgId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/actionItems?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * @param orgId {string}
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getOrgActionItemsLite(orgId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/actionItems/lite?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgActionItemsBudget(orgId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/actionItems/budget-lite?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * @param missionId {string}
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.isPrivate] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.search] {string} Searches title, description and short description.
 * @param [queryParams.tag] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.select] {string|string[]}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getMissionActionItems(missionId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/missions/${missionId}/actionItems?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * @param orgId {string}
 * @param [queryParams] {object}
 * @param [queryParams.id] {string|string[]}
 * @param [queryParams.project] {string|string[]}
 * @param [queryParams.planItem] {string|string[]}
 * @param [queryParams.action] {string|string[]}
 * @param [queryParams.fromTimesheet] {boolean}
 * @param [queryParams.status] {string|string[]}
 * @param [queryParams.person] {string|string[]}
 * @param [queryParams.startDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.endDate] {string} E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 * @param [queryParams.archived] {boolean}
 * @param [queryParams.withArchived] {boolean}
 * @param [queryParams.limit] {number}
 * @param [queryParams.sortBy] {string}
 * @param [queryParams.sortDirection] {string}
 * @param [queryParams.updatedAt] {string}  E.g. { date: 'eq 2025-07-25T00:00:00.000Z' }
 */
export function getActionItemAllocation(orgId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/actionItems/allocation?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgActionItemsProgressLite(orgId, queryParams = {}) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/actionItems/progresslite?${qs}`)
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
